import './calendar.scss'

import {
  DragAndDrop,
  type DragEventArgs,
  Inject,
  type RenderCellEventArgs,
  Resize,
  ScheduleComponent,
  ViewDirective,
  ViewsDirective,
  Week,
} from '@syncfusion/ej2-react-schedule'
import { memo, useCallback, useMemo, useRef } from 'react'

import dayjs from 'dayjs'
import { PatientScheduleSkeleton } from 'features/Home/Book/Booking/Schedule/PatientSchedule/PatientScheduleSkeleton/PatientScheduleSkeleton'
import { getDropWorkCell } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/helpers'
import { useFrozenValue } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/useFrozenValue'
import { useInteractionFreeze } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/useInteractionFreeze'
import { useScheduleCalendarReady } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/useScheduleCalendarReady'
import { useScheduleView } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/useScheduleView'
import { useScheduleColumnWidths } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/useScheduleColumnWidths'
import { useStableScheduleDataSource } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/useStableScheduleDataSource'
import { MIN_PRE_POPULATE_TIME } from 'features/Home/constants/infoConstants'
import { useParams } from 'react-router'
import { useScheduleHeight } from '../useScheduleHeight'
import { ResourceHeader } from './ResourceHeader/ResourceHeader'
import type { ScheduleCalendarProps } from './types'
import { useScheduleWeekCalendar } from './useScheduleWeekCalendar'

export const ScheduleCalendarWeek = memo(
  ({
    createCustomAppointment,
    setEditedPatient,
    handleUpdateEvents,
    showConsultationStaffInitials = false,
    prepopulateTimeOnClick = MIN_PRE_POPULATE_TIME,
    dayInterval,
    interval,
    weekDays = {},
    timeZone,
    schedules,
    patientStatuses,
    appointmentCategories,
    appointmentColor,
    hasEhrBooking,
    instanceKey,
    stacked = false,
    focusedStaffIds,
    scaleColumnsByOverlap = true,
  }: ScheduleCalendarProps) => {
    const { day, siteId } = useParams()

    const scheduleObj = useRef<ScheduleComponent>(null)
    const scheduleContainerRef = useRef<HTMLDivElement>(null)

    const formattedDate = useMemo(() => {
      if (!timeZone || !day) {
        return null
      }

      return dayjs(day).tz(timeZone, true).format('YYYY-MM-DD')
    }, [timeZone, day])

    const scheduleInstanceKey = instanceKey ?? `${siteId ?? 'site'}-${day ?? 'day'}`

    const {
      events,
      prepareScheduleChange,
      commitScheduleChange,
      handleScheduleChange,
      handleSelect,
    } = useScheduleWeekCalendar({
      schedules,
      dayInterval,
      weekDays,
      formattedDate,
      timeZone,
      scheduleObj,
      prepopulateTimeOnClick,
      createCustomAppointment,
      handleUpdateEvents,
      instanceKey,
    })

    const { isInteracting, handleInteractionStart, handleInteractionEnd } = useInteractionFreeze()
    const { isReady, markReady } = useScheduleCalendarReady(scheduleInstanceKey)

    const data = useFrozenValue(useStableScheduleDataSource(events), isInteracting)

    const { referenceWidthByColumn, setColumnReferenceWidth } = useScheduleColumnWidths('week')

    const {
      eventRendered,
      eventTemplate,
      handleEventClick,
      handleChange,
      handleDataBound,
      handleActionFailure,
      handlePopupOpen,
      reapplyAppointmentColors,
      selectedDate,
      timeScale,
      workHours,
      eventSettings,
    } = useScheduleView({
      data,
      schedules,
      scheduleObj,
      interval,
      dayInterval,
      day,
      timeZone,
      appointmentColor,
      appointmentCategories,
      patientStatuses,
      hasEhrBooking,
      showConsultationStaffInitials,
      referenceWidthByColumn,
      setColumnReferenceWidth,
      setEditedPatient,
      prepareScheduleChange,
      commitScheduleChange,
      instanceKey: scheduleInstanceKey,
      isWeekView: true,
      focusedStaffIds,
      scaleColumnsByOverlap,
    })

    const handleDragStop = useCallback(
      (args: DragEventArgs) => {
        handleInteractionEnd()

        const td = getDropWorkCell(args)
        const eventData = args.data as
          | { id?: number; start_time?: Date; end_time?: Date }
          | undefined
        const dateMs = Number(td?.getAttribute('data-date'))
        if (
          !eventData?.id ||
          !eventData.start_time ||
          !eventData.end_time ||
          Number.isNaN(dateMs)
        ) {
          requestAnimationFrame(() => reapplyAppointmentColors())
          return
        }

        const start = dayjs(eventData.start_time)
        const daysDiff = dayjs(dateMs).startOf('day').diff(start.startOf('day'), 'day')
        if (daysDiff === 0) {
          requestAnimationFrame(() => reapplyAppointmentColors())
          return
        }

        args.cancel = true
        handleScheduleChange({
          ...eventData,
          start_time: start.add(daysDiff, 'day').toDate(),
          end_time: dayjs(eventData.end_time).add(daysDiff, 'day').toDate(),
        }).finally(() => {
          requestAnimationFrame(() => reapplyAppointmentColors())
        })
      },
      [handleInteractionEnd, handleScheduleChange, reapplyAppointmentColors],
    )

    const handleCalendarDataBound = useCallback(() => {
      handleDataBound()
      markReady()
    }, [handleDataBound, markReady])

    const handleRenderCell = useCallback(
      (args: RenderCellEventArgs) => {
        if (args.elementType !== 'workCells') {
          return
        }

        // For Week view, we need to get the date from the cell
        const cellDate = dayjs(args.date).format('YYYY-MM-DD')
        const dayData = weekDays[cellDate]

        if (!dayData || !dayData.time_slots || dayData.time_slots.length === 0) {
          args.element.classList.add('e-disabled-cell')
          return
        }

        const cellStartTime = new Date(args.date)
        const cellEndTime = new Date(cellStartTime)

        // Check if this cell time falls within any of the available time slots
        const isAvailable = dayData.time_slots.some((slot: any) => {
          const slotStart = new Date(args.date)
          slotStart.setHours(
            parseInt(slot.start_time.split(':')[0], 10),
            parseInt(slot.start_time.split(':')[1], 10),
            0,
            0,
          )

          const slotEnd = new Date(args.date)
          slotEnd.setHours(
            parseInt(slot.end_time.split(':')[0], 10),
            parseInt(slot.end_time.split(':')[1], 10),
            0,
            0,
          )

          return cellStartTime >= slotStart && cellEndTime < slotEnd
        })

        if (!isAvailable) {
          args.element.classList.add('e-disabled-cell')
        } else {
          args.element.classList.remove('e-disabled-cell')
        }
      },
      [weekDays],
    )

    const scheduleHeight = useScheduleHeight(stacked)

    return (
      <div className='schedule-control-section' ref={scheduleContainerRef}>
        {!isReady && (
          <div className='schedule-skeleton-overlay'>
            <PatientScheduleSkeleton />
          </div>
        )}
        <div className={isReady ? undefined : 'schedule-pending'}>
          <div className='col-lg-12 control-section'>
            <div className={`control-wrapper ${!timeZone ? 'loading' : ''}`}>
              {timeZone && (
                <ScheduleComponent
                  key={scheduleInstanceKey}
                  ref={scheduleObj}
                  cssClass='week-schedule'
                  width='100%'
                  height={scheduleHeight}
                  timezone={timeZone}
                  timeScale={timeScale}
                  selectedDate={selectedDate}
                  startHour={dayInterval.min as string}
                  endHour={dayInterval.max as string}
                  workHours={workHours}
                  currentView='Week'
                  dateHeaderTemplate={ResourceHeader}
                  eventRendered={eventRendered}
                  showQuickInfo={false}
                  showTimeIndicator={false}
                  eventClick={handleEventClick}
                  actionBegin={handleChange}
                  dataBound={handleCalendarDataBound}
                  actionFailure={handleActionFailure}
                  showHeaderBar={false}
                  select={handleSelect}
                  popupOpen={handlePopupOpen}
                  allowDragAndDrop
                  dragStart={handleInteractionStart}
                  dragStop={handleDragStop}
                  resizeStart={handleInteractionStart}
                  resizeStop={handleInteractionEnd}
                  eventSettings={eventSettings}
                  renderCell={handleRenderCell}>
                  <ViewsDirective>
                    <ViewDirective option='Week' eventTemplate={eventTemplate} />
                  </ViewsDirective>
                  <Inject services={[Week, Resize, DragAndDrop]} />
                </ScheduleComponent>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  },
)
